import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import * as api from '../api/endpoints';

const TRIGGERS = [
  { value: 'ticket_created', label: 'Ticket created' },
  { value: 'status_changed', label: 'Status changed' },
  { value: 'staff_reply', label: 'Staff replied' },
];

const PLACEHOLDERS = [
  ['{ticket_key}', 'e.g. TK000123'],
  ['{ticket_title}', "the ticket's title"],
  ['{status}', 'current status/column name'],
  ['{due_date}', 'e.g. 01 Aug 2026, blank if none'],
  ['{customer_name}', "the customer's name"],
  ['{tracking_number}', 'any board -- blank if not filled in'],
  ['{order_number}', 'Order Fulfilment tickets only'],
  ['{product_type}', 'Order Fulfilment tickets only'],
  ['{case_and_glasses_paired}', 'Order Fulfilment tickets only'],
  ['{glasses_version}', 'Order Fulfilment tickets only'],
  ['{lens_type}', 'Order Fulfilment tickets only'],
  ['{glasses_sn}', 'Order Fulfilment tickets only'],
  ['{case_sn}', 'Order Fulfilment tickets only'],
];

// Only meaningful on a "Staff replied" template -- the reply itself and
// who wrote it aren't available on the other two triggers.
const STAFF_REPLY_PLACEHOLDERS = [
  ['{reply_body}', 'the text of the staff reply'],
  ['{staff_name}', 'who replied'],
];

const emptyForm = { name: '', subject: '', body: '', board: '', label: '' };

/**
 * Admin-only: create/edit/delete reusable email templates, mark which one
 * is active for "ticket created" / "staff replied" (only one at a time --
 * enforced server-side), and assign a "status changed" template to each
 * board's individual columns/statuses.
 *
 * The ticket key in the subject and the "you can reply" hint in the body
 * are always added automatically by the backend regardless of what's
 * typed here, so a template can't accidentally break the inbound reply
 * loop -- worth mentioning in the UI so admins don't feel they need to
 * type those themselves.
 */
const EmailTemplateManagerModal = ({ onClose }) => {
  const [trigger, setTrigger] = useState('ticket_created');
  const [templates, setTemplates] = useState([]);
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  // Lets the "Available placeholders" list actually be clickable: whichever
  // subject/body field was last focused (create form or the one currently
  // being edited) is where a clicked placeholder gets inserted, at the
  // cursor position rather than just tacked onto the end.
  const [activeField, setActiveField] = useState('createBody');
  const createSubjectRef = useRef(null);
  const createBodyRef = useRef(null);
  const editSubjectRef = useRef(null);
  const editBodyRef = useRef(null);
  const fieldRefs = { createSubject: createSubjectRef, createBody: createBodyRef, editSubject: editSubjectRef, editBody: editBodyRef };

  const insertPlaceholder = (token) => {
    // If nothing's being edited, "editSubject"/"editBody" from a previous
    // (now-closed) edit session would point nowhere useful -- fall back to
    // the create form instead.
    const field = (!editingId && activeField.startsWith('edit')) ? 'createBody' : activeField;
    const isEdit = field.startsWith('edit');
    const isSubject = field.endsWith('Subject');
    const key = isSubject ? 'subject' : 'body';
    const setter = isEdit ? setEditForm : setForm;
    const el = fieldRefs[field].current;

    if (el && typeof el.selectionStart === 'number') {
      const start = el.selectionStart;
      const end = el.selectionEnd;
      setter((p) => ({ ...p, [key]: (p[key] || '').slice(0, start) + token + (p[key] || '').slice(end) }));
      requestAnimationFrame(() => {
        el.focus();
        const pos = start + token.length;
        el.setSelectionRange(pos, pos);
      });
    } else {
      setter((p) => ({ ...p, [key]: (p[key] || '') + token }));
    }
  };

  const load = () => {
    setLoading(true);
    Promise.all([api.getEmailTemplates(), api.getBoards()])
      .then(([tpls, brds]) => { setTemplates(tpls); setBoards(brds); })
      .catch(() => toast.error('Could not load email templates.'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const templatesForTrigger = templates.filter((t) => t.trigger === trigger);
  const statusTemplates = templates.filter((t) => t.trigger === 'status_changed');

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.subject.trim() || !form.body.trim()) {
      toast.error('Name, subject, and body are all required.');
      return;
    }
    try {
      setCreating(true);
      await api.createEmailTemplate({ ...form, trigger });
      setForm(emptyForm);
      load();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Could not create template.');
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (template) => {
    setEditingId(template.id);
    setEditForm({
      name: template.name, subject: template.subject, body: template.body,
      board: template.board || '', label: template.label || '',
    });
  };

  const handleSaveEdit = async () => {
    if (!editForm.name.trim() || !editForm.subject.trim() || !editForm.body.trim()) {
      toast.error('Name, subject, and body cannot be blank.');
      return;
    }
    try {
      setSaving(true);
      await api.updateEmailTemplate(editingId, editForm);
      setEditingId(null);
      load();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Could not update template.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Delete this template? Any status it\'s assigned to will just stop using a template (falling back to the default wording).')) return;
    try {
      await api.deleteEmailTemplate(templateId);
      if (editingId === templateId) setEditingId(null);
      load();
    } catch (error) {
      toast.error('Could not delete template.');
    }
  };

  const handleSetActive = async (templateId) => {
    try {
      await api.updateEmailTemplate(templateId, { is_active: true });
      load();
    } catch (error) {
      toast.error('Could not activate template.');
    }
  };

  const handleAssignColumn = async (columnId, templateId) => {
    try {
      await api.setColumnEmailTemplate(columnId, templateId || null);
      load();
    } catch (error) {
      toast.error('Could not assign template.');
    }
  };

  const handleAssignCreationColumn = async (columnId, templateId) => {
    try {
      await api.setColumnCreationEmailTemplate(columnId, templateId || null);
      load();
    } catch (error) {
      toast.error('Could not assign template.');
    }
  };

  const handleAssignBoardDefault = async (boardKey, templateId) => {
    try {
      await api.setBoardDefaultStatusChangedTemplate(boardKey, templateId || null);
      load();
    } catch (error) {
      toast.error('Could not assign the board default template.');
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 720 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 style={{ margin: 0 }}>Email templates</h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">&times;</button>
        </div>

        <p style={{ fontSize: '0.8rem', color: '#aab7c4' }}>
          The ticket key (e.g. <code>[TK000123]</code>) and a "you can reply to this email" line are always
          added automatically -- you don't need to include them yourself.
        </p>

        <div className="segmented-control">
          {TRIGGERS.map((t) => (
            <button key={t.value} type="button" className={trigger === t.value ? 'active' : ''} onClick={() => setTrigger(t.value)}>
              {t.label}
            </button>
          ))}
        </div>

        {loading ? <p>Loading...</p> : (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1rem' }}>
              {templatesForTrigger.length === 0 && <p style={{ color: '#aab7c4' }}>No templates yet for this trigger.</p>}
              {templatesForTrigger.map((template) => (
                <div key={template.id} style={{ background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', padding: '0.75rem' }}>
                  {editingId === template.id ? (
                    <>
                      <input className="form-input" style={{ marginBottom: '0.4rem' }} value={editForm.name}
                        onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))} placeholder="Template name" />
                      <input ref={editSubjectRef} className="form-input" style={{ marginBottom: '0.4rem' }} value={editForm.subject}
                        onChange={(e) => setEditForm((p) => ({ ...p, subject: e.target.value }))} onFocus={() => setActiveField('editSubject')} placeholder="Subject" />
                      <textarea ref={editBodyRef} className="form-textarea" style={{ minHeight: 100, marginBottom: '0.4rem' }} value={editForm.body}
                        onChange={(e) => setEditForm((p) => ({ ...p, body: e.target.value }))} onFocus={() => setActiveField('editBody')} placeholder="Body" />
                      {trigger !== 'status_changed' && (
                        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.4rem' }}>
                          <select className="form-select" style={{ flex: 1 }} value={editForm.board}
                            onChange={(e) => setEditForm((p) => ({ ...p, board: e.target.value }))}>
                            <option value="">Any board</option>
                            {boards.map((b) => <option key={b.key} value={b.id}>{b.name}</option>)}
                          </select>
                          <input className="form-input" style={{ flex: 1 }} value={editForm.label}
                            onChange={(e) => setEditForm((p) => ({ ...p, label: e.target.value }))} placeholder="Label (e.g. Repair) -- any label if blank" />
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button type="button" className="btn-primary" onClick={handleSaveEdit} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
                        <button type="button" className="btn-secondary" onClick={() => setEditingId(null)} disabled={saving}>Cancel</button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <strong>{template.name}</strong>
                        <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                          {trigger !== 'status_changed' && !template.board_key && !template.label && (
                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.8rem', cursor: 'pointer' }}>
                              <input type="radio" name={`active-${trigger}`} checked={template.is_active} onChange={() => handleSetActive(template.id)} />
                              Active
                            </label>
                          )}
                          <button type="button" className="btn-secondary" onClick={() => startEdit(template)}>Edit</button>
                          <button type="button" className="btn-danger" onClick={() => handleDelete(template.id)}>Delete</button>
                        </div>
                      </div>
                      <p style={{ fontSize: '0.8rem', color: '#aab7c4', margin: '0.3rem 0 0 0' }}>{template.subject}</p>
                      {trigger !== 'status_changed' && (template.board_key || template.label) ? (
                        <p style={{ fontSize: '0.75rem', color: 'var(--accent, #6aa9ff)', margin: '0.2rem 0 0 0' }}>
                          Applies to: {template.board_key || 'any board'}{template.label ? ` / label "${template.label}"` : ''}
                        </p>
                      ) : trigger !== 'status_changed' ? (
                        <p style={{ fontSize: '0.75rem', color: '#aab7c4', margin: '0.2rem 0 0 0' }}>Applies to: any board/label (global)</p>
                      ) : null}
                    </>
                  )}
                </div>
              ))}
            </div>

            <div className="section-title">New {TRIGGERS.find((t) => t.value === trigger).label.toLowerCase()} template</div>
            <form onSubmit={handleCreate}>
              <input className="form-input" style={{ marginBottom: '0.4rem' }} value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} placeholder="Template name (e.g. 'Friendly shipping update')" disabled={creating} />
              <input ref={createSubjectRef} className="form-input" style={{ marginBottom: '0.4rem' }} value={form.subject}
                onChange={(e) => setForm((p) => ({ ...p, subject: e.target.value }))} onFocus={() => setActiveField('createSubject')}
                placeholder="Subject, e.g. Update on {ticket_title}" disabled={creating} />
              <textarea ref={createBodyRef} className="form-textarea" style={{ minHeight: 100, marginBottom: '0.4rem' }} value={form.body}
                onChange={(e) => setForm((p) => ({ ...p, body: e.target.value }))} onFocus={() => setActiveField('createBody')}
                placeholder={'Body, e.g.\nHi {customer_name},\n\nYour ticket {ticket_key} is now {status}.'} disabled={creating} />
              {trigger !== 'status_changed' && (
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.4rem' }}>
                  <select className="form-select" style={{ flex: 1 }} value={form.board} disabled={creating}
                    onChange={(e) => setForm((p) => ({ ...p, board: e.target.value }))}>
                    <option value="">Any board</option>
                    {boards.map((b) => <option key={b.key} value={b.id}>{b.name}</option>)}
                  </select>
                  <input className="form-input" style={{ flex: 1 }} value={form.label} disabled={creating}
                    onChange={(e) => setForm((p) => ({ ...p, label: e.target.value }))} placeholder="Label (e.g. Repair) -- any label if blank" />
                </div>
              )}
              <p style={{ fontSize: '0.75rem', color: '#aab7c4', margin: '-0.1rem 0 0.4rem' }}>
                {trigger !== 'status_changed'
                  ? 'Leave board and label blank for a general template. A template targeting a specific board and/or label wins over a general one.'
                  : null}
              </p>
              <button type="submit" className="btn-primary" disabled={creating}>{creating ? 'Creating...' : 'Create template'}</button>
            </form>

            {trigger === 'status_changed' && (
              <>
                <div className="section-title">Board defaults</div>
                <p style={{ fontSize: '0.78rem', color: '#aab7c4', margin: '-0.3rem 0 0.5rem' }}>
                  Used whenever a ticket moves to a status on this board that doesn't have its own template assigned
                  below -- lets you set up "any status change on this board sends this" in one place. A status's own
                  template (below) always wins if both could apply.
                </p>
                {boards.map((board) => (
                  <div key={`default-${board.key}`} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <span style={{ width: 160, fontSize: '0.85rem', fontWeight: 600 }}>{board.name}</span>
                    <select
                      className="form-select"
                      style={{ flex: 1 }}
                      value={board.default_status_changed_email_template_id || ''}
                      onChange={(e) => handleAssignBoardDefault(board.key, e.target.value ? Number(e.target.value) : null)}
                    >
                      <option value="">No board default (falls back to plain wording)</option>
                      {statusTemplates.map((t) => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </select>
                  </div>
                ))}

                <div className="section-title">Assign to statuses</div>
                {boards.map((board) => (
                  <div key={board.key} style={{ marginBottom: '0.75rem' }}>
                    <p style={{ fontWeight: 600, fontSize: '0.85rem', margin: '0 0 0.35rem' }}>{board.name}</p>
                    {board.columns.map((col) => (
                      <div key={col.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.3rem' }}>
                        <span style={{ width: 160, fontSize: '0.85rem', color: '#aab7c4' }}>{col.name}</span>
                        <select
                          className="form-select"
                          style={{ flex: 1 }}
                          value={col.email_template_id || ''}
                          onChange={(e) => handleAssignColumn(col.id, e.target.value ? Number(e.target.value) : null)}
                        >
                          <option value="">Default wording (no template)</option>
                          {statusTemplates.map((t) => (
                            <option key={t.id} value={t.id}>{t.name}</option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>
                ))}
              </>
            )}

            {trigger === 'ticket_created' && (
              <>
                <div className="section-title">Assign to a status (when created directly there)</div>
                <p style={{ fontSize: '0.78rem', color: '#aab7c4', margin: '-0.3rem 0 0.5rem' }}>
                  This is for when a ticket is created STARTING on that status (e.g. an order placed straight into
                  "Assembling" or "Testing") -- separate from a template targeting a board/label above, and separate
                  from the "Status changed" template a column uses later if a ticket merely moves there. Beats any
                  board/label template if both could apply.
                </p>
                {boards.map((board) => (
                  <div key={board.key} style={{ marginBottom: '0.75rem' }}>
                    <p style={{ fontWeight: 600, fontSize: '0.85rem', margin: '0 0 0.35rem' }}>{board.name}</p>
                    {board.columns.map((col) => (
                      <div key={col.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.3rem' }}>
                        <span style={{ width: 160, fontSize: '0.85rem', color: '#aab7c4' }}>{col.name}</span>
                        <select
                          className="form-select"
                          style={{ flex: 1 }}
                          value={col.creation_email_template_id || ''}
                          onChange={(e) => handleAssignCreationColumn(col.id, e.target.value ? Number(e.target.value) : null)}
                        >
                          <option value="">No column-specific template (use board/label/active)</option>
                          {templatesForTrigger.map((t) => (
                            <option key={t.id} value={t.id}>{t.name}</option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>
                ))}
              </>
            )}

            <div className="section-title">Available placeholders</div>
            <p style={{ fontSize: '0.75rem', color: '#aab7c4', margin: '-0.3rem 0 0.4rem' }}>
              Click one to insert it into the subject/body field you were last typing in.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.3rem 1rem', fontSize: '0.78rem', color: '#aab7c4' }}>
              {(trigger === 'staff_reply' ? [...PLACEHOLDERS, ...STAFF_REPLY_PLACEHOLDERS] : PLACEHOLDERS).map(([token, desc]) => (
                <button
                  key={token}
                  type="button"
                  onClick={() => insertPlaceholder(token)}
                  style={{
                    display: 'flex', justifyContent: 'flex-start', gap: '0.4rem', textAlign: 'left',
                    background: 'transparent', border: 'none', padding: '0.1rem 0', cursor: 'pointer',
                    color: 'inherit', font: 'inherit',
                  }}
                  title={`Insert ${token}`}
                >
                  <code style={{ color: 'var(--accent, #6aa9ff)' }}>{token}</code>
                  <span>-- {desc}</span>
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default EmailTemplateManagerModal;
